import React, { useState, useRef, useEffect, useMemo } from "react";
import { Grid, Typography, Box, useMediaQuery, Tabs, Tab, Chip, IconButton, Card, Drawer, List, ListItemButton, ListItemIcon, ListItemText, Divider } from "@mui/material";
import PriceCard from "./PriceCard";
import MySvgIcons from '../Shared/paymentMethods';
import { useTheme } from "@mui/material/styles";
import { useTranslation } from "../../hooks/useTranslation";
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import ArrowBackIosNewRoundedIcon from '@mui/icons-material/ArrowBackIosNewRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';

const subcategoryOptions = {
  Food: [
    "Main Plates",
    "Snacks & Fries",
    "Burgers & Kebab",
    "Soups",
    "Pierogi & Regional",
    "Desserts",
    "Sweets",
  ],
  Grill: [
    "Main Plates",
    "Snacks & Fries",
    "Burgers & Kebab",
    "Soups",
    "Pierogi & Regional",
    "Desserts",
    "Sweets",
  ],
  Drink: ["Hot", "Cold", "Alcoholic", "Shots"],
};

function PricingSection({items}) {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery('(max-width:899px)');
  const isXsScreen = useMediaQuery('(max-width:599px)');
  const [expandedItemId, setExpandedItemId] = useState(null);
  const [tabValue, setTabValue] = useState('drink');
  const [selectedSubcategory, setSelectedSubcategory] = useState("");
  const theme = useTheme();
  const tabsRef = useRef(null);
  // const tabsListRef = useRef(null);
  const [shouldCenterSubTabs, setShouldCenterSubTabs] = useState(true);
  const sectionRefs = useRef({});
  const selectedSubcategoryRef = useRef('');
  const scrollRafRef = useRef(null);
  const isProgrammaticScrollRef = useRef(false);
  const programmaticTargetSubRef = useRef(null);
  const programmaticTimeoutRef = useRef(null);

  const [mobileView, setMobileView] = useState('home'); // 'home' | 'category'
  const [mobileCategory, setMobileCategory] = useState(null); // 'menu' | 'grill' | 'drinks' | 'alcohol'
  const [subcategoryPickerOpen, setSubcategoryPickerOpen] = useState(false);

  const mobileChipsScrollerRef = useRef(null);
  const mobileChipRefs = useRef({});

  const [appBarHeight, setAppBarHeight] = useState(isXsScreen ? 56 : 64);
  const mobileStickyBarRef = useRef(null);
  const [mobileStickyBarHeight, setMobileStickyBarHeight] = useState(112);

  useEffect(() => {
    const measure = () => {
      const header = document?.querySelector?.('header.MuiAppBar-root');
      const next = header?.getBoundingClientRect?.().height;
      if (next && Number.isFinite(next)) {
        setAppBarHeight(Math.round(next));
      } else {
        setAppBarHeight(isXsScreen ? 56 : 64);
      }
    };

    // measure on mount and when switching views
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, [isXsScreen, mobileView]);
  
  // Helper function to translate subcategories
  const translateSubcategory = (subcategory) => {
    const subcategoryMap = {
      "Main Plates": t('mainPlates'),
      "Snacks & Fries": t('snacksAndFries'),
      "Burgers & Kebab": t('burgersAndKebab'),
      "Soups": t('soups'),
      "Pierogi & Regional": t('pierogiAndRegional'),
      "Desserts": t('desserts'),
      "Sweets": t('sweets'),
      "Hot": t('hot'),
      "Cold": t('cold'),
      "Alcoholic": t('alcoholic'),
      "Shots": t('shots'),
    };
    return subcategoryMap[subcategory] || subcategory;
  };
  // const swipeStartX = useRef(null);
  // const swipeEndX = useRef(null);
  // const swipeStartY = useRef(null);
  // const swipeEndY = useRef(null);

  const handleCardExpand = (itemId) => {
    setExpandedItemId(prevId => (prevId === itemId ? null : itemId)); // Toggle expanded state
  };

  const handleChange = (event, newValue) => {
    setTabValue(newValue);
    setSelectedSubcategory("");
    

    setTimeout(() => {
      if (tabsRef.current) {
        const rect = tabsRef.current.getBoundingClientRect();
        window.scrollTo({
          top: rect.top + window.scrollY - 60,
          behavior: "smooth"
        });
      }
    }, 100);
  };

  const handleSubcategoryChange = (event, newValue) => {
    // "all" means no specific subcategory filter
    setSelectedSubcategory(newValue);
    //uncomment to have 'all food and all drinks' tab
    // setSelectedSubcategory(newValue === "all" ? "" : newValue);
    setTimeout(() => {
      if (tabsRef.current) {
        const rect = tabsRef.current.getBoundingClientRect();
        window.scrollTo({
          top: rect.top + window.scrollY - 60,
          behavior: "smooth"
        });
      }
    }, 100);
  };

  // const handleTouchStart = (e) => {
  //   swipeStartX.current = e.touches[0].clientX;
  //   swipeStartY.current = e.touches[0].clientY;
  // };

  // const handleTouchEnd = (e) => {
  //   swipeEndX.current = e.changedTouches[0].clientX;
  //   swipeEndY.current = e.changedTouches[0].clientY;
  //   const swipeDistance = swipeStartX.current - swipeEndX.current;
  //   const swipeDistanceY = swipeStartY.current - swipeEndY.current;

  //   if (Math.abs(swipeDistanceY) < 100) {
  //       if (swipeDistance > 150) {
  //         // Swiped left: food -> drink
  //         setTabValue((prevValue) => (prevValue === "food" ? "drink" : prevValue));
  //       } else if (swipeDistance < -150) {
  //         // Swiped right: drink -> food
  //         setTabValue((prevValue) => (prevValue === "drink" ? "food" : prevValue));
  //       }

  //       if (swipeDistance > 150 || swipeDistance < -150) {
  //         setSelectedSubcategory(""); // reset subcategory on swipe change
  //         setTimeout(() => {
  //           if (tabsRef.current) {
  //             const rect = tabsRef.current.getBoundingClientRect();
  //             window.scrollTo({
  //               top: rect.top + window.scrollY - 60,
  //               behavior: "smooth",
  //             });
  //           }
  //         }, 100);
  //       }
  //     }
  //   };

  const filterItems = () => {
    if (!items) return [];

    const type = tabValue === "food" ? "Food" : tabValue === "grill" ? "Grill" : "Drink";
    let filteredItems = items.filter((item) => item.itemType === type);

    // Filter by subcategory (if selected)
    if (selectedSubcategory) {
      return filteredItems.filter(
        (item) => item.itemSubcategory === selectedSubcategory
      );
    }

    // No subcategory selected => sort by subcategory order
    const order = subcategoryOptions[type] || [];
    const orderIndex = new Map(order.map((name, idx) => [name, idx]));

    // stable-ish tie-breaker so items within same subcategory don’t jump around
    return filteredItems
      .map((item, originalIndex) => ({ item, originalIndex }))
      .sort((a, b) => {
        const aRank = orderIndex.has(a.item.itemSubcategory)
          ? orderIndex.get(a.item.itemSubcategory)
          : Number.POSITIVE_INFINITY;

        const bRank = orderIndex.has(b.item.itemSubcategory)
          ? orderIndex.get(b.item.itemSubcategory)
          : Number.POSITIVE_INFINITY;

        if (aRank !== bRank) return aRank - bRank;

        // keep original order inside the same subcategory
        return a.originalIndex - b.originalIndex;
      })
      .map(({ item }) => item);
  };

  //uncomment to have 'all food and all drinks' tab
  // const showFoodSubcategories = tabValue === "food";

  const currentSubcategoryKey =
  tabValue === "food" ? "Food" : tabValue === "grill" ? "Grill" : tabValue === "drink" ? "Drink" : null;

  const typeLabel = tabValue === "food" ? "Food" : tabValue === "grill" ? "Grill" : "Drink";

  // Only show subcategory tabs that have at least 1 AVAILABLE item in that subcategory
  const availableSubcategories = (subcategoryOptions[typeLabel] || []).filter((sub) =>
    (items || []).some(
      (item) =>
        item.itemType === typeLabel &&
        item.itemSubcategory === sub &&
        item.itemAvailable === true // adjust if your availability field differs
    )
  );

  const itemsBySubcategory = useMemo(() => {
    const map = new Map();
    for (const sub of availableSubcategories) map.set(sub, []);
    (items || [])
      .filter((item) => item.itemType === typeLabel && item.itemAvailable === true)
      .forEach((item) => {
        if (!map.has(item.itemSubcategory)) return;
        map.get(item.itemSubcategory).push(item);
      });
    return map;
  }, [items, typeLabel, availableSubcategories]);

  const scrollToSubcategory = (sub) => {
    const el = sectionRefs.current?.[sub];
    if (!el) return;
    const topOffset = 64 + 56; // navbar + sticky tabs/chips area
    const rect = el.getBoundingClientRect();
    const top = rect.top + window.scrollY - topOffset;
    window.scrollTo({ top, behavior: 'smooth' });
    setSelectedSubcategory(sub);
  };

  useEffect(() => {
    selectedSubcategoryRef.current = selectedSubcategory;
  }, [selectedSubcategory]);

  useEffect(() => {
    return () => {
      if (programmaticTimeoutRef.current) {
        window.clearTimeout(programmaticTimeoutRef.current);
        programmaticTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    // Desktop-only: keep selectedSubcategory consistent with the visible tabs.
    // On mobile we use a different (category-specific) subcategory list.
    if (isSmallScreen) return;
    if (selectedSubcategory && !availableSubcategories.includes(selectedSubcategory)) {
      setSelectedSubcategory("");
    }
  }, [selectedSubcategory, availableSubcategories, isSmallScreen]);

  useEffect(() => {
    const tabsRoot = tabsRef.current;
    if (!tabsRoot) return;

    // MUI's scroll container
    const scroller = tabsRoot.querySelector(".MuiTabs-scroller");
    if (!scroller) return;

    setShouldCenterSubTabs(scroller.scrollWidth <= scroller.clientWidth);
  }, [availableSubcategories, isSmallScreen]);

  const allMobileItems = useMemo(() => items || [], [items]);
  const foodItemsMobile = useMemo(
    () => allMobileItems.filter((i) => i.itemType === 'Food' && i.itemAvailable === true),
    [allMobileItems]
  );
  const drinkItemsMobile = useMemo(
    () => allMobileItems.filter((i) => i.itemType === 'Drink' && i.itemAvailable === true),
    [allMobileItems]
  );
  const grillItemsMobile = useMemo(
    () => allMobileItems.filter((i) => i.itemType === 'Grill' && i.itemAvailable === true),
    [allMobileItems]
  );
  const grillSubcategoriesMobile = useMemo(() => {
    const preferredOrder = subcategoryOptions.Grill || [];
    const orderIndex = new Map(preferredOrder.map((name, idx) => [name, idx]));

    return Array.from(new Set(grillItemsMobile.map((i) => i.itemSubcategory).filter(Boolean))).sort((a, b) => {
      const aRank = orderIndex.has(a) ? orderIndex.get(a) : Number.POSITIVE_INFINITY;
      const bRank = orderIndex.has(b) ? orderIndex.get(b) : Number.POSITIVE_INFINITY;
      if (aRank !== bRank) return aRank - bRank;
      return String(a).localeCompare(String(b));
    });
  }, [grillItemsMobile]);
  const drinksItemsMobile = useMemo(
    () => drinkItemsMobile.filter((i) => i.itemSubcategory === 'Hot' || i.itemSubcategory === 'Cold'),
    [drinkItemsMobile]
  );
  const alcoholItemsMobile = useMemo(
    () => drinkItemsMobile.filter((i) => i.itemSubcategory === 'Alcoholic' || i.itemSubcategory === 'Shots'),
    [drinkItemsMobile]
  );

  const categoryConfigMobile = useMemo(
    () => ({
      menu: {
        title: t('food'),
        subcategories: subcategoryOptions.Food,
        items: foodItemsMobile,
      },
      grill: {
        title: t('grill'),
        subcategories: grillSubcategoriesMobile,
        items: grillItemsMobile,
      },
      drinks: {
        title: t('drinks'),
        subcategories: ['Hot', 'Cold'],
        items: drinksItemsMobile,
      },
      alcohol: {
        title: t('alcohol'),
        subcategories: ['Alcoholic', 'Shots'],
        items: alcoholItemsMobile,
      },
    }),
    [t, foodItemsMobile, grillItemsMobile, grillSubcategoriesMobile, drinksItemsMobile, alcoholItemsMobile]
  );

  const currentMobileCategory = mobileCategory ? categoryConfigMobile[mobileCategory] : null;

  const mobileAvailableSubcategories = useMemo(() => {
    if (!currentMobileCategory) return [];
    return (currentMobileCategory.subcategories || []).filter((sub) =>
      (currentMobileCategory.items || []).some((i) => i.itemSubcategory === sub)
    );
  }, [currentMobileCategory]);

  useEffect(() => {
    if (!isSmallScreen) return;
    if (mobileView !== 'category') return;

    const measure = () => {
      const el = mobileStickyBarRef.current;
      const next = el?.getBoundingClientRect?.().height;
      if (next && Number.isFinite(next)) {
        setMobileStickyBarHeight(Math.max(88, Math.round(next)));
      }
    };

    const id = window.requestAnimationFrame(measure);
    window.addEventListener('resize', measure);
    return () => {
      window.cancelAnimationFrame(id);
      window.removeEventListener('resize', measure);
    };
  }, [isSmallScreen, mobileView, mobileCategory, mobileAvailableSubcategories.length]);

  const mobileItemsBySubcategory = useMemo(() => {
    const map = new Map();
    for (const sub of mobileAvailableSubcategories) map.set(sub, []);
    (currentMobileCategory?.items || []).forEach((item) => {
      if (!map.has(item.itemSubcategory)) return;
      map.get(item.itemSubcategory).push(item);
    });
    return map;
  }, [currentMobileCategory, mobileAvailableSubcategories]);

  useEffect(() => {
    if (!isSmallScreen) return;
    if (mobileView !== 'category') return;
    if (!mobileAvailableSubcategories || mobileAvailableSubcategories.length === 0) return;

    const topOffset = appBarHeight + mobileStickyBarHeight;
    const activeThreshold = 20;

    // Scroll listener (deterministic and matches the sticky layout)
    const computeActive = () => {
      if (isProgrammaticScrollRef.current) return;
      const subs = mobileAvailableSubcategories;
      let active = subs[0];

      for (const sub of subs) {
        const el = sectionRefs.current?.[sub];
        if (!el) continue;
        const top = el.getBoundingClientRect().top;
        if (top - topOffset <= activeThreshold) {
          active = sub;
        } else {
          break;
        }
      }

      if (active && active !== selectedSubcategoryRef.current) {
        selectedSubcategoryRef.current = active;
        setSelectedSubcategory(active);
      }
    };

    const onScroll = () => {
      if (scrollRafRef.current) return;
      scrollRafRef.current = window.requestAnimationFrame(() => {
        scrollRafRef.current = null;

        if (isProgrammaticScrollRef.current) {
          const targetSub = programmaticTargetSubRef.current;
          const targetEl = targetSub ? sectionRefs.current?.[targetSub] : null;
          if (targetEl) {
            const delta = Math.abs(targetEl.getBoundingClientRect().top - topOffset);
            if (delta < activeThreshold) {
              isProgrammaticScrollRef.current = false;
              programmaticTargetSubRef.current = null;
              if (programmaticTimeoutRef.current) {
                window.clearTimeout(programmaticTimeoutRef.current);
                programmaticTimeoutRef.current = null;
              }
            }
          }
        }

        computeActive();
      });
    };

    // Listen on both window and document (capture) to handle nested scroll containers.
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('scroll', onScroll, { passive: true, capture: true });
    // initialize highlight to first/closest section
    computeActive();

    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('scroll', onScroll, { capture: true });
      if (scrollRafRef.current) {
        window.cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, [isSmallScreen, mobileView, mobileAvailableSubcategories, appBarHeight, mobileStickyBarHeight]);

  useEffect(() => {
    if (!isSmallScreen) return;
    if (mobileView !== 'category') return;
    if (!mobileAvailableSubcategories || mobileAvailableSubcategories.length === 0) return;

    const activeSub = selectedSubcategory || mobileAvailableSubcategories[0];
    const scrollerEl = mobileChipsScrollerRef.current;
    const chipEl = activeSub ? mobileChipRefs.current?.[activeSub] : null;
    if (!scrollerEl || !chipEl) return;

    const containerRect = scrollerEl.getBoundingClientRect();
    const chipRect = chipEl.getBoundingClientRect();

    const leftBound = containerRect.left + 8;
    const rightBound = containerRect.right - 8;
    const isOutOfView = chipRect.left < leftBound || chipRect.right > rightBound;
    if (!isOutOfView) return;

    const desiredLeft = containerRect.left + containerRect.width / 2 - chipRect.width / 2;
    const delta = chipRect.left - desiredLeft;
    scrollerEl.scrollTo({ left: Math.max(0, scrollerEl.scrollLeft + delta), behavior: 'smooth' });
  }, [isSmallScreen, mobileView, mobileAvailableSubcategories, selectedSubcategory]);

  if (isSmallScreen) {
    const openCategory = (categoryKey) => {
      setMobileCategory(categoryKey);
      setSelectedSubcategory('');
      sectionRefs.current = {};
      setMobileView('category');
      // jump to top
      window.scrollTo({ top: 0, behavior: 'auto' });
    };

    const closeCategory = () => {
      setMobileView('home');
      setMobileCategory(null);
      setSelectedSubcategory('');
      sectionRefs.current = {};
      setSubcategoryPickerOpen(false);
      window.scrollTo({ top: 0, behavior: 'auto' });
    };

    const scrollToMobileSubcategory = (sub) => {
      const el = sectionRefs.current?.[sub];
      if (!el) return;
      const topOffset = appBarHeight + mobileStickyBarHeight;

      // Lock highlight while smooth scrolling so it doesn't flash back.
      isProgrammaticScrollRef.current = true;
      programmaticTargetSubRef.current = sub;
      if (programmaticTimeoutRef.current) window.clearTimeout(programmaticTimeoutRef.current);
      programmaticTimeoutRef.current = window.setTimeout(() => {
        isProgrammaticScrollRef.current = false;
        programmaticTargetSubRef.current = null;
        programmaticTimeoutRef.current = null;
      }, 2000);

      // Use scrollIntoView so it works even if the page is inside a scroll container.
      // scrollMarginTop on the target sections handles the sticky header offset.
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      selectedSubcategoryRef.current = sub;
      setSelectedSubcategory(sub);
    };

    const Section = ({ title, onArrowClick, sectionItems }) => {
      const previewItems = (() => {
        const list = Array.isArray(sectionItems) ? sectionItems : [];
        const getSortPrice = (item) => {
          const details = Array.isArray(item?.itemDetails) ? item.itemDetails : [];
          let maxPrice = 0;
          for (const detail of details) {
            const price = Number(detail?.itemPrice) || 0;
            if (price > maxPrice) maxPrice = price;
          }
          return maxPrice;
        };

        return list
          .map((item, originalIndex) => ({ item, originalIndex, price: getSortPrice(item) }))
          .sort((a, b) => {
            if (b.price !== a.price) return b.price - a.price;
            return a.originalIndex - b.originalIndex;
          })
          .map(({ item }) => item)
          .slice(0, 15);
      })();
      const showSeeAll = (sectionItems || []).length > previewItems.length;

      return (
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1, mb: 1 }}>
            <Typography variant="h2">{title}</Typography>
            <IconButton onClick={onArrowClick} aria-label={t('open')}>
              <ChevronRightRoundedIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              WebkitOverflowScrolling: 'touch',
              px: 1,
              pb: 1,
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
            }}
          >
            {previewItems.map((item) => (
              <Box key={item._id} sx={{ minWidth: 210, maxWidth: 210, flex: '0 0 auto' }}>
                <PriceCard
                  key={`${item._id}-${item.itemImage}`}
                  itemId={item._id}
                  itemName={item.itemName}
                  itemImage={item.itemImage}
                  itemType={item.itemType}
                  itemDetails={item.itemDetails}
                  itemIncludes={item.itemIncludes}
                  itemAvailable={item.itemAvailable}
                  itemSubCategory={item.itemSubcategory}
                  expanded={false}
                  onExpand={() => {}}
                  mobile
                  mobileVariant="card"
                />
              </Box>
            ))}

            {showSeeAll && (
              <Box sx={{ minWidth: 210, maxWidth: 210, flex: '0 0 auto' }}>
                <Card
                  onClick={onArrowClick}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') onArrowClick();
                  }}
                  sx={{
                    height: '100%',
                    minHeight: 300,
                    background: theme.palette.component.componentBackground,
                    borderRadius: '18px',
                    border: `1px solid ${theme.palette.component.borderColor}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <Box
                      sx={{
                        width: 72,
                        height: 72,
                        borderRadius: '999px',
                        backgroundColor: theme.palette.common.white,
                        border: `1px solid ${theme.palette.component.borderColor}`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <ChevronRightRoundedIcon />
                    </Box>
                    <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                      See All
                    </Typography>
                  </Box>
                </Card>
              </Box>
            )}
          </Box>
        </Box>
      );
    };

    if (mobileView === 'home') {
      return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', position: 'relative' }}>
          <Grid container sx={{ width: '100%', margin: '0 auto', display: 'flex', justifyContent: 'center', padding: '20px 0px' }}>
            <Grid item xs={12}>
              <Grid container sx={{ width: '100%', margin: '0 auto', display: 'flex', justifyContent: 'center', padding: '0px 0px' }}>
                <Grid item xs={11}>
                  <Box sx={{ paddingBottom: '1rem' }}>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('home')}
                    </Typography>
                    <Typography variant="h1" gutterBottom>
                      {t('orderOnline')}
                    </Typography>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('orderToGoPaySecurely')}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ px: 1 }}>
                    <Section
                      title={t('food')}
                      onArrowClick={() => openCategory('menu')}
                      sectionItems={foodItemsMobile}
                    />
                    <Section
                      title={t('grill')}
                      onArrowClick={() => openCategory('grill')}
                      sectionItems={grillItemsMobile}
                    />
                    <Section
                      title={t('drinks')}
                      onArrowClick={() => openCategory('drinks')}
                      sectionItems={drinksItemsMobile}
                    />
                    <Section
                      title={t('alcohol')}
                      onArrowClick={() => openCategory('alcohol')}
                      sectionItems={alcoholItemsMobile}
                    />

                    <Box sx={{ px: 1, pt: 1, pb: 3, display: 'flex', justifyContent: 'center' }}>
                      <MySvgIcons />
                    </Box>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
      );
    }

    // category view
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', position: 'relative' }}>
        <Grid container sx={{ width: '100%', margin: '0 auto', display: 'flex', justifyContent: 'center', padding: '20px 0px' }}>
          <Grid item xs={12}>
            <Grid container sx={{ width: '100%', margin: '0 auto', display: 'flex', justifyContent: 'center', padding: '0px 0px' }}>
              <Grid item xs={12} sx={{ px: 1 }}>
                <Box
                  ref={mobileStickyBarRef}
                  sx={{
                    position: 'sticky',
                    top: `${appBarHeight}px`,
                    zIndex: theme.zIndex.appBar,
                    backgroundColor: theme.palette.common.background,
                    borderBottom: `1px solid ${theme.palette.component.borderColor}`,
                  }}
                >
                  <Grid container sx={{ width: '100%', margin: '0 auto', display: 'flex', justifyContent: 'center' }}>
                    <Grid item xs={11}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.5 }}>
                        <IconButton onClick={closeCategory} aria-label={t('back')}>
                          <ArrowBackIosNewRoundedIcon />
                        </IconButton>
                        <Typography variant="h1">{currentMobileCategory?.title || t('food')}</Typography>
                      </Box>

                      {mobileAvailableSubcategories.length > 0 && (
                        <Typography
                          variant="subtitle1"
                          sx={{
                            pb: 0.5,
                            pl: 6.25,
                            fontWeight: 700,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }}
                        >
                          {translateSubcategory(selectedSubcategory || mobileAvailableSubcategories[0])}
                        </Typography>
                      )}
                    </Grid>
                  </Grid>

                  {mobileAvailableSubcategories.length > 0 && (
                    <Box sx={{ px: 1, pb: 1, pt: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box
                          sx={{
                            flex: 1,
                            overflowX: 'auto',
                            whiteSpace: 'nowrap',
                            WebkitOverflowScrolling: 'touch',
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none',
                            '&::-webkit-scrollbar': { display: 'none' },
                          }}
                          ref={mobileChipsScrollerRef}
                        >
                          {mobileAvailableSubcategories.map((sub) => (
                            <Chip
                              key={sub}
                              label={translateSubcategory(sub)}
                              clickable
                              onClick={() => scrollToMobileSubcategory(sub)}
                              ref={(node) => {
                                if (node) mobileChipRefs.current[sub] = node;
                              }}
                              sx={{
                                mr: 1,
                                borderRadius: '999px',
                                height: 36,
                                maxWidth: 190,
                                '& .MuiChip-label': {
                                  whiteSpace: 'nowrap',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                },
                                backgroundColor:
                                  selectedSubcategory === sub
                                    ? theme.palette.common.offBlack
                                    : theme.palette.common.white,
                                color:
                                  selectedSubcategory === sub
                                    ? theme.palette.common.white
                                    : theme.palette.common.offBlack,
                              }}
                            />
                          ))}
                        </Box>

                        <IconButton
                          onClick={() => setSubcategoryPickerOpen(true)}
                          aria-label={t('open')}
                          sx={{
                            width: 44,
                            height: 44,
                            borderRadius: '14px',
                            backgroundColor: theme.palette.common.white,
                            border: `1px solid ${theme.palette.component.borderColor}`,
                            flexShrink: 0,
                          }}
                        >
                          <ExpandMoreRoundedIcon />
                        </IconButton>
                      </Box>
                    </Box>
                  )}
                </Box>

                <Drawer
                  anchor="bottom"
                  open={subcategoryPickerOpen}
                  onClose={() => setSubcategoryPickerOpen(false)}
                  PaperProps={{
                    sx: {
                      borderTopLeftRadius: '20px',
                      borderTopRightRadius: '20px',
                      backgroundColor: theme.palette.common.background,
                    },
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Typography variant="h3" sx={{ fontWeight: 800 }}>
                        {currentMobileCategory?.title || t('food')}
                      </Typography>
                      <IconButton
                        onClick={() => setSubcategoryPickerOpen(false)}
                        aria-label={t('close')}
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: '999px',
                          backgroundColor: theme.palette.common.white,
                          border: `1px solid ${theme.palette.component.borderColor}`,
                        }}
                      >
                        <CloseRoundedIcon />
                      </IconButton>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <List sx={{ p: 0 }}>
                      {mobileAvailableSubcategories.map((sub) => {
                        const isActive = selectedSubcategory === sub;
                        return (
                          <ListItemButton
                            key={sub}
                            onClick={() => {
                              scrollToMobileSubcategory(sub);
                              setSubcategoryPickerOpen(false);
                            }}
                            sx={{
                              borderRadius: '14px',
                              mb: 1,
                              backgroundColor: theme.palette.common.white,
                              border: `1px solid ${theme.palette.component.borderColor}`,
                            }}
                          >
                            <ListItemText
                              primary={translateSubcategory(sub)}
                              primaryTypographyProps={{ sx: { fontWeight: 700 } }}
                            />
                            {isActive && (
                              <ListItemIcon sx={{ minWidth: 32, color: theme.palette.common.offBlack }}>
                                <CheckRoundedIcon />
                              </ListItemIcon>
                            )}
                          </ListItemButton>
                        );
                      })}
                    </List>
                  </Box>
                </Drawer>

                {mobileAvailableSubcategories.map((sub) => (
                  <Box
                    key={sub}
                    data-sub={sub}
                    ref={(node) => {
                      if (node) sectionRefs.current[sub] = node;
                    }}
                    sx={{ mb: 2, scrollMarginTop: `${appBarHeight + mobileStickyBarHeight + 12}px` }}
                  >
                    <Typography variant="h2" sx={{ px: 1, pb: 1 }}>
                      {translateSubcategory(sub)}
                    </Typography>

                    {(mobileItemsBySubcategory.get(sub) || []).map((item) => (
                      <Box key={item._id} sx={{ px: 1, pb: 1 }}>
                        <PriceCard
                          key={`${item._id}-${item.itemImage}`}
                          itemId={item._id}
                          itemName={item.itemName}
                          itemImage={item.itemImage}
                          itemType={item.itemType}
                          itemDetails={item.itemDetails}
                          itemIncludes={item.itemIncludes}
                          itemAvailable={item.itemAvailable}
                          itemSubCategory={item.itemSubcategory}
                          expanded={false}
                          onExpand={() => {}}
                          mobile
                          mobileVariant="row"
                        />
                      </Box>
                    ))}
                  </Box>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
    );
  }

  return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        position: 'relative',
      }}>

        <Grid container  sx={{
          width: "100%",
          margin: "0 auto",
          display: "flex",
          justifyContent: "center",
          padding: "20px 0px",
        }}>
          <Grid item xs={12}>
            <Grid
              container
              sx={{
                width: isSmallScreen ? "100%" : "90%",
                margin: "0 auto",
                display: "flex",
                justifyContent: "center",
                padding: "0px 0px",
              }}
            >
              <Grid item xs={11} sm={11} md={11} lg={11} xl={9.1}>
                <Box sx={{ paddingBottom: '2rem' }}>
                <Typography variant="h1" gutterBottom>
                  {t('orderOnline')}
                </Typography>
                </Box>
              </Grid>

              <Grid item xs={11} sm={11} md={11} lg={11} xl={9.1}>
                <Typography variant="subtitle1" gutterBottom>
                  {t('orderToGoPaySecurely')}
                </Typography>
              </Grid>

              <Grid item xs={11} sm={11} md={11} lg={11} xl={9.1} sx={{paddingBottom: '1rem'}}>
                <MySvgIcons />
              </Grid>
              <Grid
                ref={tabsRef}
                container
                sx={{
                  width: isSmallScreen ? "100%" : "90%",
                  margin: "0 auto",
                  display: "flex",
                  justifyContent: "center",
                  padding: "20px 0px",
                }}
                // onTouchStart={handleTouchStart}
                // onTouchEnd={handleTouchEnd}
              >
              {/* Main tabs: Food / Grill / Drink */}
              <Tabs
                value={tabValue}
                onChange={handleChange}
                centered
                sx={{
                  width: "98.5%",
                  position: "sticky",
                  top: "64px",
                  zIndex: 2,
                  border: `1px solid ${theme.palette.component.borderColor}`,
                  backgroundColor:
                    theme.palette.component.componentBackground,
                  boxShadow: `0 2px 4px ${theme.palette.component.borderColor}`,
                }}
              >
                <Tab label={t('food')} value="food" />
                <Tab label={t('grill')} value="grill" />
                <Tab label={t('drink')} value="drink" />
              </Tabs>

              {/* Subcategory tabs (for current main tab) */}
              {currentSubcategoryKey && availableSubcategories.length > 0 && (
                <Tabs
                  value={selectedSubcategory || false}
                  onChange={handleSubcategoryChange}
                  variant="scrollable"
                  scrollButtons="auto"
                  ref={tabsRef}
                  sx={{
                    width: "98.5%",
                    mb: 2,
                    border: `1px solid ${theme.palette.component.borderColor}`,
                    position: "sticky",
                    top: "64px",
                    zIndex: 2,
                    backgroundColor: theme.palette.component.componentBackground,
                    boxShadow: `0 2px 4px ${theme.palette.component.borderColor}`,
                    "& .MuiTabs-flexContainer": {
                      justifyContent: shouldCenterSubTabs ? "center" : "flex-start",
                    },
                  }}
                  TabScrollButtonProps={{
                    sx: { display: shouldCenterSubTabs ? "none" : undefined },
                  }}
                >
                  {availableSubcategories.map((sub) => (
                    <Tab key={sub} label={translateSubcategory(sub)} value={sub} />
                  ))}
                </Tabs>
              )}

              {filterItems().map((item) => (
                <Grid
                  key={item._id}
                  item
                  xs={12}
                  sm={12}
                  md={6}
                  lg={3}
                  xl={3}
                  sx={{ padding: "0px 5px" }}
                >
                  <PriceCard
                    key={`${item._id}-${item.itemImage}`}
                    itemId={item._id}
                    itemName={item.itemName}
                    itemImage={item.itemImage}
                    itemType={item.itemType}
                    itemDetails={item.itemDetails}
                    itemIncludes={item.itemIncludes}
                    itemAvailable={item.itemAvailable}
                    itemSubCategory={item.itemSubcategory}
                    expanded={expandedItemId === item._id}
                    onExpand={() => handleCardExpand(item._id)}
                  />
                </Grid>
              ))}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}


export default PricingSection;